import { appData } from 'appData';
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import * as types from 'types';
import { sendForm } from 'utils/sendForm';
import { ErrorResponse, Referral, ReferralReportAppendix } from 'types';
import { fetchOneWithAction } from './fetchOne';
import { patchReferralAction } from './referral';
import { patchOne } from './patchOne';

type UseAppendixPatchParams = Partial<ReferralReportAppendix>;

type UseAppendixOptions = UseMutationOptions<
  ReferralReportAppendix,
  unknown,
  UseAppendixPatchParams
>;

export const usePatchAppendixAction = (
  options?: UseAppendixOptions,
  action?: string,
) => {
  return useMutation<ReferralReportAppendix, unknown, UseAppendixPatchParams>({
    mutationFn: (appendix) => patchAppendixAction(appendix, action),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      if (options?.onSuccess) {
        options.onSuccess(data, variables, onMutateResult, context);
      }
    },
  });
};

export const patchAppendixAction = (
  appendix: Partial<ReferralReportAppendix>,
  action?: string,
) => {
  return patchOne({
    id: String(appendix.id),
    name: 'referralreportappendices',
    action,
    payload: appendix,
  });
};

type UseUpdateAppendixError = ErrorResponse;
type UseUpdateAppendixData = [string, string | File | string[]][];

type UseUpdateAppendixOptions = UseMutationOptions<
  types.ReferralReportAppendix,
  UseUpdateAppendixError,
  UseUpdateAppendixData
>;

export const useUpdateAppendix = (
  url: string,
  queryKey: string,
  options?: UseUpdateAppendixOptions,
) => {
  const queryClient = useQueryClient();
  return useMutation<
    types.ReferralReportAppendix,
    UseUpdateAppendixError,
    UseUpdateAppendixData
  >({
    mutationFn: (data) =>
      sendForm({
        headers: { Authorization: `Token ${appData.token}` },
        keyValuePairs: data,
        url,
        action: 'PUT',
      }),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, onMutateResult, context);
      }
    },
  });
};

type UseAddAppendixError = ErrorResponse;
type UseAddAppendixData = [string, string | File | string[]][];

type UseAddAppendixOptions = UseMutationOptions<
  types.ReferralReportAppendix,
  UseAddAppendixError,
  UseAddAppendixData
>;

export const useAddAppendix = (
  url: string,
  queryKey: string,
  options?: UseAddAppendixOptions,
) => {
  const queryClient = useQueryClient();
  return useMutation<
    types.ReferralReportAppendix,
    UseAddAppendixError,
    UseAddAppendixData
  >({
    mutationFn: (data) =>
      sendForm({
        headers: { Authorization: `Token ${appData.token}` },
        keyValuePairs: data,
        url,
        action: 'POST',
      }),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, onMutateResult, context);
      }
    },
  });
};

type UseSubFiltersNoteLitesActionParams = {
  filter: string;
  query: string;
};

type UseAppendixValidatorsActionOptions = UseMutationOptions<
  any,
  unknown,
  UseSubFiltersNoteLitesActionParams
>;

export const useAppendixValidatorsAction = (
  options?: UseAppendixValidatorsActionOptions,
) => {
  return useMutation<any, unknown, any>({
    mutationFn: ({ appendix }) => appendixValidatorsAction(appendix),
    onSuccess: (data, variables, onMutateResult, context) => {
      if (options?.onSuccess) {
        options.onSuccess(data, variables, onMutateResult, context);
      }
    },
  });
};

export const appendixValidatorsAction = (appendix: ReferralReportAppendix) => {
  return fetchOneWithAction({
    queryKey: ['referralreportappendices', appendix.id, 'get_validators'],
    meta: undefined,
  } as any);
};
